import type {FieldFilterOperator} from '@xh/hoist/data';

/**
 * GridSpec - a plain-JSON, versioned description of a configurable grid.
 *
 * A GridSpec is the single source of truth shared by the manual grid configurator, the AI grid
 * assistant and the grid's own interactions (resize, reorder, sort). None of them touch the
 * GridModel directly: all changes are expressed as GridSpecOps (see GridSpecOps.ts), applied by one
 * reducer, and the resulting spec is bound to the GridModel by GridSpecBinder.
 *
 * Everything here must stay JSON-serializable - no functions. Anything that needs code (renderers,
 * class rules) is described by a named descriptor and resolved in spec/Formats.ts / GridSpecBuilder.ts.
 *
 * NOTE: the AI grid assistant's output schema, gridSpecOps.schema.json, describes these types and the ops in
 * GridSpecOps.ts; the client sends it with each request (rzx-backend/docs/decisions/0027). Keep it in step with
 * any change here. Bump GRID_SPEC_VERSION on any breaking change, and add an upgrade step to normalizeSpec().
 */
export const GRID_SPEC_VERSION = 2;

// Field catalog and formats are shared with the other component specs
import type {FormatSpec} from '../spec/SpecTypes';
export type {DateStyle, FieldCatalog, FieldCatalogEntry, FieldDataType, FormatSpec, FormatType, NumberStyle} from '../spec/SpecTypes';

//------------------------
// Style rules - declarative replacement for ag-Grid cell/row class rule functions
//------------------------
export const FONT_WEIGHTS = ['light', 'normal', 'semibold', 'bold'] as const;
export type FontWeight = (typeof FONT_WEIGHTS)[number];

export const FONT_STYLES = ['normal', 'italic', 'underline', 'strikethrough'] as const;
export type FontStyle = (typeof FONT_STYLES)[number];

/** Text colors - each maps to a Hoist theme var (see GridSpec.scss), so they work in light and dark themes. */
export const STYLE_COLORS = ['positive', 'negative', 'primary', 'warning', 'accent', 'muted'] as const;
export type StyleColor = (typeof STYLE_COLORS)[number];

/** Background colors - translucent Hoist intent colors (see GridSpec.scss). */
export const STYLE_BACKGROUNDS = ['positive', 'negative', 'primary', 'warning', 'neutral'] as const;
export type StyleBackground = (typeof STYLE_BACKGROUNDS)[number];

/** The style a rule applies. Each part is optional - omit it to leave that part unchanged. */
export interface CellStyle {
    fontWeight?: FontWeight;
    fontStyle?: FontStyle;
    color?: StyleColor;
    background?: StyleBackground;
}

export type StylePart = keyof CellStyle;

export const CELL_STYLE_VALUES: {[K in keyof Required<CellStyle>]: readonly CellStyle[K][]} = {
    fontWeight: FONT_WEIGHTS,
    fontStyle: FONT_STYLES,
    color: STYLE_COLORS,
    background: STYLE_BACKGROUNDS
};

/** Operators allowed in filters and style rule conditions (a subset of Hoist FieldFilter operators). */
export const CONDITION_OPERATORS: FieldFilterOperator[] = [
    '=',
    '!=',
    '>',
    '>=',
    '<',
    '<=',
    'like',
    'not like',
    'begins',
    'not begins',
    'ends',
    'not ends',
    'includes',
    'excludes'
];

export interface StyleCondition {
    field: string;
    op: FieldFilterOperator;
    value: any;
}

/** When several matching rules set the same part of the style, the last one wins. */
export interface StyleRule {
    when: StyleCondition;
    style: CellStyle;
}

//------------------------
// Columns
//------------------------
export const AGGREGATORS = ['sum', 'avg', 'min', 'max', 'count', 'first', 'last'] as const;
export type Aggregator = (typeof AGGREGATORS)[number];

export type ColumnAlign = 'left' | 'center' | 'right';

export interface GridSpecColumn {
    /** Catalog field name. Also serves as the column id. */
    field: string;
    headerName?: string;
    hidden?: boolean;
    /** Fixed width in pixels. Ignored when `flex` is set. */
    width?: number;
    flex?: boolean;
    align?: ColumnAlign;
    headerAlign?: ColumnAlign;
    pinned?: 'left' | 'right';
    /** Omit to keep the host's default rendering for the field. */
    format?: FormatSpec;
    /** How the column is aggregated in the totals row. Omit for no total. */
    aggregator?: Aggregator;
    /** Conditional cell styles. */
    cellClassRules?: StyleRule[];
    /** Show the formatted value as a tooltip. */
    tooltip?: boolean;
}

export interface GridSpecColumnGroup {
    groupId: string;
    headerName: string;
    headerAlign?: ColumnAlign;
    /** Leaf columns only - groups are one level deep. */
    children: GridSpecColumn[];
}

export type GridSpecColumnOrGroup = GridSpecColumn | GridSpecColumnGroup;

export function isColumnGroup(c: GridSpecColumnOrGroup): c is GridSpecColumnGroup {
    return !!c && 'groupId' in c;
}

//------------------------
// Grid-level
//------------------------
export interface GridSpecSorter {
    colId: string;
    sort: 'asc' | 'desc';
    /** Sort by absolute value. */
    abs?: boolean;
}

export interface GridSpecFilter {
    field: string;
    op: FieldFilterOperator;
    value: any;
}

export type GridSpecTotals = false | 'top' | 'bottom';
export type GridSpecSizingMode = 'tiny' | 'compact' | 'standard' | 'large';

/** When columns are autosized to fit their content - mirrors Hoist's GridAutosizeMode. */
export const AUTOSIZE_MODES = ['disabled', 'onDemand', 'onSizingModeChange', 'managed'] as const;
export type GridSpecAutosizeMode = (typeof AUTOSIZE_MODES)[number];

/** How autosized columns grow to fill any remaining grid width - mirrors GridAutosizeOptions.fillMode. */
export const AUTOSIZE_FILL_MODES = ['none', 'all', 'left', 'right'] as const;
export type GridSpecAutosizeFillMode = (typeof AUTOSIZE_FILL_MODES)[number];

/** Whitelisted plain ag-Grid options - passed as `agOptions` to the grid component. */
export interface GridSpecAgOptions {
    headerHeight?: number;
    animateRows?: boolean;
    suppressRowHoverHighlight?: boolean;
    enableCellTextSelection?: boolean;
}

export const AG_OPTION_KEYS: (keyof GridSpecAgOptions)[] = ['headerHeight', 'animateRows', 'suppressRowHoverHighlight', 'enableCellTextSelection'];

export interface GridSpec {
    version: number;
    columns: GridSpecColumnOrGroup[];
    sortBy: GridSpecSorter[];
    groupBy: string[];
    /** Tree grids only - include individual (leaf) rows under each group. */
    showLeafRows: boolean;
    totals: GridSpecTotals;
    filters: GridSpecFilter[];
    rowBorders: boolean;
    cellBorders: boolean;
    stripeRows: boolean;
    /** Null to follow the app-wide sizing mode. */
    sizingMode: GridSpecSizingMode;
    /** Null to keep the host grid's autosize mode. */
    autosizeMode: GridSpecAutosizeMode;
    /** Null to keep the host grid's fill mode. */
    autosizeFillMode: GridSpecAutosizeFillMode;
    rowClassRules: StyleRule[];
    agOptions: GridSpecAgOptions;
}

/**
 * What the host grid supports. Drives which configurator sections are shown, what the AI is told it
 * may do, and how grouping / filtering are applied.
 *  - 'flat': applied to the GridModel/Store by the binder (ag-Grid row grouping, Store filter).
 *  - 'external': the host applies it (e.g. to a Cube View query) by observing the spec.
 *  - 'none': not supported.
 */
export interface GridSpecCapabilities {
    grouping: 'flat' | 'external' | 'none';
    filtering: 'flat' | 'external' | 'none';
    totals: boolean;
    /** Tree grids with a Cube view - support for `showLeafRows`. */
    leafRows: boolean;
    /** False when the host sorts the grid itself - no Sort section, and the grid's sort isn't synced to the spec. Defaults to true. */
    sorting?: boolean;
}

export const DEFAULT_GRID_SPEC: Omit<GridSpec, 'columns'> = {
    version: GRID_SPEC_VERSION,
    sortBy: [],
    groupBy: [],
    showLeafRows: true,
    totals: false,
    filters: [],
    rowBorders: false,
    cellBorders: false,
    stripeRows: true,
    sizingMode: null,
    autosizeMode: null,
    autosizeFillMode: null,
    rowClassRules: [],
    agOptions: {}
};
